import { computeEventSlots } from './diagramLayout.js'

const generateId = () => globalThis.crypto.randomUUID()

const buildGroupsMutationInput = (workflowId, expectedVersion, groups, updateNote, domainEventInputs) => {
  const input = {
    id: workflowId,
    expectedVersion,
    groups,
    updateNote
  }
  if (domainEventInputs && domainEventInputs.length > 0) {
    input.domainEvents = domainEventInputs
  }

  return input
}

export const buildCreateGroupMutation = ({ workflow, workflowId, name }) => {
  const groups = workflow.groups || []

  if (groups.find((g) => g.name === name)) {
    return { error: `Group "${name}" already exists` }
  }

  const domainEvents = workflow.domainEvents || []
  let startSlotX = 0

  // Place the new group to the right of every existing event and group.
  if (groups.length > 0) {
    const slots = computeEventSlots(domainEvents, groups)
    const maxSlot = Math.max(...slots.values(), 0)
    const maxGroupStartSlotX = Math.max(...groups.map((g) => g.startSlotX))
    startSlotX = Math.max(maxGroupStartSlotX + 1, maxSlot + 1)
  }

  const newGroup = { id: generateId(), name, startSlotX }
  const updatedGroups = [...groups, newGroup]

  return {
    mutationInput: buildGroupsMutationInput(workflowId, workflow.version, updatedGroups, {
      action: 'created group',
      target: name
    }),
    name
  }
}

export const buildUpdateGroupMutation = ({ workflow, workflowId, group, name }) => {
  const groups = workflow.groups || []
  const groupIndex = groups.findIndex((g) => g.name === group)

  if (groupIndex === -1) {
    return { error: `Group "${group}" not found` }
  }

  const matchedGroup = groups[groupIndex]

  if (groups.find((g) => g.name === name && g.id !== matchedGroup.id)) {
    return { error: `Group "${name}" already exists` }
  }

  const oldName = matchedGroup.name
  const updatedGroups = [...groups]
  updatedGroups[groupIndex] = { ...matchedGroup, name }

  return {
    mutationInput: buildGroupsMutationInput(workflowId, workflow.version, updatedGroups, {
      action: 'renamed group',
      target: `${oldName} → ${name}`
    }),
    oldName,
    name
  }
}

export const buildDeleteGroupMutation = ({ workflow, workflowId, group }) => {
  const groups = workflow.groups || []
  const groupIndex = groups.findIndex((g) => g.name === group)

  if (groupIndex === -1) {
    return { error: `Group "${group}" not found` }
  }

  const matchedGroup = groups[groupIndex]
  const domainEvents = workflow.domainEvents || []

  const updatedGroups = groups.filter((g) => g.id !== matchedGroup.id)

  const groupIdsToClear = new Set([matchedGroup.id])

  const sortedGroups = [...groups].sort((a, b) => a.startSlotX - b.startSlotX)
  const deletedIsFirst = sortedGroups[0]?.id === matchedGroup.id

  if (deletedIsFirst) {
    const nextGroup = sortedGroups[1]
    if (nextGroup) {
      const newFirstIndex = updatedGroups.findIndex((g) => g.id === nextGroup.id)
      if (updatedGroups[newFirstIndex].startSlotX !== 0) {
        updatedGroups[newFirstIndex] = { ...updatedGroups[newFirstIndex], startSlotX: 0 }
      }
      groupIdsToClear.add(nextGroup.id)
    }
  }

  const domainEventInputs = []
  for (let i = 0; i < domainEvents.length; i++) {
    const event = domainEvents[i]
    if (event.groupId && groupIdsToClear.has(event.groupId)) {
      domainEventInputs.push({
        id: event.id,
        index: i,
        groupId: null,
        expectedVersion: event.version || undefined
      })
    }
  }

  return {
    mutationInput: buildGroupsMutationInput(
      workflowId,
      workflow.version,
      updatedGroups,
      {
        action: 'deleted group',
        target: group
      },
      domainEventInputs
    ),
    group
  }
}
